/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * useFavicon Hook - Dynamic Favicon Management
 * ============================================================================
 *
 * Updates the browser tab favicon based on the current module configuration.
 * Supports SVG favicons for all CoreX modules.
 *
 * ============================================================================
 */

import { useEffect } from 'react';
import { getConfig } from '@/config/project.config';

/**
 * Hook to dynamically update the favicon based on the current module
 */
export function useFavicon() {
  useEffect(() => {
    const config = getConfig();
    const iconPath = config.logos.icon || '/favicons/corex-favicon.svg';

    // Find existing favicon link or create one
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    // Update favicon
    link.type = 'image/svg+xml';
    link.href = iconPath;

    // Also update the page title to match the module
    document.title = config.name;
  }, []);
}

/**
 * Set favicon programmatically (for dynamic module switching)
 */
export function setFavicon(iconPath: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.type = 'image/svg+xml';
  link.href = iconPath;
}

export default useFavicon;
